import { AccessLevel } from '@dev/ee-api/ee-api-models';
import { flatten, isEmpty } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { parse } from 'query-string';

import dayjs from '@api/services/tools/dayjs';
import { parseUrlParams } from '@api/services/tools/handleUrlParams';
import { getAccountStore } from '@api/store/account/actions';
import { useEventsFilterOptions } from '../hooks/useEventsFilterOptions';
import { useCampaignPopoverOptions } from './useCampaignPopoverOptions';
import { useChannelPopoverOptions } from './useChannelPopoverOptions';
import { useDateDropdownOptions } from './useDateDropdownOptions';
import { DateDropdownOptions } from '@api/services/dictionaries/DateRangeValues';
import { ClearChannels, LoadChannelsStart, getChannelsStore } from '@api/store/channels/actions';
import { INITIAL_PER_PAGE } from '@api/store/exports/types';
import { useDateParamsBasedOnLogsRetention } from './useDateParamsBasedOnLogsRetention';
import { useCallback, useState } from 'react';

enum MobileFilterTabsTypes {
    DATE = 'date',
    CHANNEL = 'channel',
    EVENT = 'event',
    CAMPAIGN = 'campaign',
}

const defaultFilters: Record<string, boolean> = {
    [MobileFilterTabsTypes.CHANNEL]: true,
    [MobileFilterTabsTypes.CAMPAIGN]: true,
};

export const useMobileFilterTabs = (availableFilters = defaultFilters): any => {
    const [searchChannel, setSearchChannel] = useState('');
    const channelsOptions = useChannelPopoverOptions('');
    const { GeneralInfo } = useSelector(getAccountStore);
    const { hasMore, channels } = useSelector(getChannelsStore);
    const campaignsOptions = useCampaignPopoverOptions();
    const dateOptions = useDateDropdownOptions(GeneralInfo.AccountAccessLevel?.includes(AccessLevel.ExtendedLogs));
    const defaultValues = useLogsValuesFromUrl(dateOptions);
    const eventOptions = useEventsFilterOptions();
    const query = parse(location.search);
    const startDate = query?.from?.toString();
    const endDate = query?.to?.toString();
    const dispatch = useDispatch();
    const { from, to } = useDateParamsBasedOnLogsRetention(true);
    const enabledCampaignFilter =
        GeneralInfo?.AccountAccessLevel.includes(AccessLevel.ViewCampaigns) &&
        availableFilters?.[MobileFilterTabsTypes.CAMPAIGN] &&
        !isEmpty(campaignsOptions);

    const { t } = useTranslation();

    const minDate = dayjs().utc().startOf('day').subtract(GeneralInfo.KeepLogsForDays, 'day').toJSON() as any;
    const maxDate = dayjs().utc().startOf('day').add(1, 'day').toJSON() as any;

    const availableCampaigns = enabledCampaignFilter ? campaignsOptions : [];
    const availableChannels = availableFilters?.[MobileFilterTabsTypes.CHANNEL] ? channelsOptions : [];
    const channelOptionsBasedOnAL = [...availableChannels, ...availableCampaigns].sort();

    const combineLabel = enabledCampaignFilter ? t('channels_campaigns') : t('channels');

    const fetchNextChannels = useCallback(
        (search: string, customOffset?: number) => {
            const offset = customOffset ?? channels?.length;
            !offset && dispatch(ClearChannels());
            (hasMore || !offset) &&
                dispatch(LoadChannelsStart({ limit: INITIAL_PER_PAGE, offset, search: searchChannel, skipSagaURL: true, from, to }));
        },
        [channels?.length]
    );

    return [
        {
            id: MobileFilterTabsTypes.DATE,
            label: t('daterange') as string,
            defaultValue: defaultValues.date,
            options: dateOptions,
            type: 'date' as any,
            minDate,
            maxDate,
            startDate,
            endDate,
        },
        ...(availableFilters?.[MobileFilterTabsTypes.CHANNEL]
            ? [
                  {
                      id: MobileFilterTabsTypes.CHANNEL,
                      defaultValue: defaultValues.channel,
                      label: combineLabel,
                      options: channelOptionsBasedOnAL,
                      type: 'radio' as any,
                      infiniteScrollProps: {
                          next: fetchNextChannels,
                          hasMore: true,
                          dataLength: availableChannels?.length || INITIAL_PER_PAGE,
                      },
                      //   searchValue: 'asd',
                      onSearchChange: (tab, search) => {
                          setSearchChannel(search);
                          fetchNextChannels(null, 0);
                      },
                  },
              ]
            : []),
        {
            id: MobileFilterTabsTypes.EVENT,
            defaultCheckboxValue: [...(defaultValues.statuses || []), ...(defaultValues.result || [])],
            label: t('event') as string,
            options: eventOptions,
            type: 'checkbox' as any,
        },
    ];
};

const useLogsValuesFromUrl = (dateOptions = useDateDropdownOptions(true)) => {
    const query = parseUrlParams<{ channel: string; campaign: string }>(location.search);
    const urlStatuses: string[] = query.statuses ? flatten([query.statuses]) : null;
    const urlResult: string[] = query.result ? flatten([query.result]) : null;
    let date = '7';

    if (query?.from && query?.to) {
        const from = dayjs.utc(query?.from as string);
        const to = dayjs.utc(query?.to as string);
        const dateDiff = to.diff(from, 'day');
        const dateValue = dateOptions?.find((el) => +el.value === +dateDiff)?.value || DateDropdownOptions.Calendar;
        date = dayjs().isSame(to, 'day') ? dateValue : DateDropdownOptions.Calendar;
    }

    return {
        date,
        channel: query.channel || 'all',
        campaign: query.campaign || 'all',
        statuses: urlStatuses,
        result: urlResult,
    };
};
